import { Box, Container, Grid, Typography } from "@mui/material";
import React from "react";
import SettingsIcon from "@mui/icons-material/Settings";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import PsychologyIcon from "@mui/icons-material/Psychology";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";

const offers = [
  {
    icon: <SettingsIcon sx={{ fontSize: 26 }} />,
    bg: "linear-gradient(135deg,#4F6EF7 0%,#7C3AED 100%)",
    glow: "rgba(79,110,247,0.3)",
    title: "Effortless School Management",
    description:
      "Automate attendance, grading, timetables, reporting, and administrative workflows — allowing educators to focus on teaching, not paperwork.",
  },
  {
    icon: <NotificationsActiveIcon sx={{ fontSize: 26 }} />,
    bg: "linear-gradient(135deg,#0EA5E9 0%,#4F6EF7 100%)",
    glow: "rgba(14,165,233,0.3)",
    title: "Real-Time Parent Engagement",
    description:
      "Keep parents informed with instant updates on academic progress, attendance, announcements, and school activities through our connected parent platform.",
  },
  {
    icon: <PsychologyIcon sx={{ fontSize: 26 }} />,
    bg: "linear-gradient(135deg,#10B981 0%,#0EA5E9 100%)",
    glow: "rgba(16,185,129,0.3)",
    title: "AI-Powered Learning Support",
    description:
      "Provide students with intelligent academic assistance, personalized insights, and real-time doubt resolution powered by advanced AI technology.",
  },
  {
    icon: <SportsEsportsIcon sx={{ fontSize: 26 }} />,
    bg: "linear-gradient(135deg,#F59E0B 0%,#EF4444 100%)",
    glow: "rgba(245,158,11,0.3)",
    title: "Interactive & Gamified Learning",
    description:
      "Boost engagement through skill-based quizzes, competitive challenges, and interactive learning experiences that make education enjoyable and effective.",
  },
];

const OfferSection = () => {
  return (
    <Container maxWidth="lg" id="what_we_offer">
      {/* Header */}
      <Box textAlign="center" mb={{ xs: 7, md: 9 }}>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 0.8,
            borderRadius: "100px",
            bgcolor: "rgba(79,110,247,0.08)",
            border: "1px solid rgba(79,110,247,0.2)",
            mb: 3,
          }}
        >
          <Typography fontSize="0.82rem" fontWeight={600} color="#4F6EF7" letterSpacing="0.04em">
            WHAT WE OFFER
          </Typography>
        </Box>

        <Typography
          variant="h2"
          fontWeight={900}
          color="#0D1B4B"
          gutterBottom
          sx={{ fontSize: { xs: "1.8rem", md: "2.8rem" }, mb: 2 }}
        >
          Everything Your School Needs
        </Typography>

        <Typography
          variant="body1"
          color="#64748B"
          sx={{ maxWidth: 700, mx: "auto", lineHeight: 1.8 }}
        >
          A powerful digital ecosystem designed to simplify school operations,
          strengthen parent engagement, and enhance student learning through
          intelligent technology.
        </Typography>
      </Box>

      {/* Cards grid */}
      <Grid container spacing={3}>
        {offers.map((item, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Box
              sx={{
                p: { xs: 4, md: 5 },
                borderRadius: "22px",
                border: "1px solid rgba(13,27,75,0.07)",
                bgcolor: "#F8FAFF",
                height: "100%",
                position: "relative",
                overflow: "hidden",
                transition: "all 0.35s cubic-bezier(0.4,0,0.2,1)",
                "&:hover": {
                  transform: "translateY(-6px)",
                  boxShadow: `0 24px 64px ${item.glow}`,
                  borderColor: "rgba(79,110,247,0.15)",
                  bgcolor: "#fff",
                },
              }}
            >
              {/* Top-right gradient orb */}
              <Box
                sx={{
                  position: "absolute",
                  top: -30,
                  right: -30,
                  width: 120,
                  height: 120,
                  borderRadius: "50%",
                  background: item.bg,
                  opacity: 0.06,
                  filter: "blur(20px)",
                  pointerEvents: "none",
                }}
              />

              {/* Icon */}
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 52,
                  height: 52,
                  borderRadius: "14px",
                  background: item.bg,
                  color: "#fff",
                  mb: 3,
                  boxShadow: `0 8px 20px ${item.glow}`,
                }}
              >
                {item.icon}
              </Box>

              <Typography
                variant="h5"
                fontWeight={700}
                color="#0D1B4B"
                gutterBottom
                sx={{ mb: 1.5 }}
              >
                {item.title}
              </Typography>

              <Typography
                variant="body2"
                color="#475569"
                sx={{ lineHeight: 1.8, fontSize: "0.95rem" }}
              >
                {item.description}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Mission block */}
      <Box
        mt={{ xs: 8, md: 10 }}
        p={{ xs: 5, md: 7 }}
        borderRadius="24px"
        textAlign="center"
        sx={{
          background: "linear-gradient(135deg, #F0F4FF 0%, #EEF2FF 100%)",
          border: "1px solid rgba(79,110,247,0.12)",
        }}
      >
        <Typography
          variant="body1"
          sx={{
            maxWidth: 820,
            mx: "auto",
            lineHeight: 1.9,
            fontSize: "1.05rem",
            color: "#475569",
            mb: 3,
          }}
        >
          We believe in the power of technology to transform education.
          Our mission is to empower schools, engage parents, and support
          students with innovative tools that make learning smarter,
          more connected, and future-ready.
        </Typography>

        <Typography
          variant="h5"
          fontWeight={700}
          sx={{
            background: "linear-gradient(135deg, #4F6EF7 0%, #7C3AED 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          Join us in shaping the future of connected education.
        </Typography>
      </Box>
    </Container>
  );
};

export default OfferSection;
